/** Sugiyama-based layout engine for V4 attunement tree node graphs — pure computation, no Phaser dependency. */
import type { V4TreeDefinition, V4NodeDefinition, V4ApotheosisDefinition } from '../utils/Types';

/** Returns pixel width of text at the given font size. Injected by renderer. */
export type MeasureTextFn = (text: string, fontSize: string) => number;

/** V4 tier approximation for layout purposes. */
export type LayoutTier = 'foundation' | 'threshold' | 'transcendent' | 'apotheosis';

/** Positioned node output — renderer adds visual state (locked/chosen/equipped). */
export interface LayoutNode {
  nodeId: string;
  tier: LayoutTier;
  x: number;
  y: number;
  nodeType: string;
}

/** Available rendering area for the tree. */
export interface LayoutBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Layout constants
const MIN_NODE_GAP = 40;
const LABEL_PADDING = 10;
const BOUNDS_MARGIN = 8;

/**
 * Computes 2D positions for all nodes in a V4 attunement tree.
 * Linear chains run vertically; fork points fan out into parallel columns.
 */
export class TreeLayoutEngine {
  /** Compute positions for all nodes in a V4 tree within the given bounds. */
  layout(
    treeDef: V4TreeDefinition,
    bounds: LayoutBounds,
    measureText: MeasureTextFn,
    fontSize: string,
  ): Map<string, LayoutNode> {
    const result = new Map<string, LayoutNode>();
    const allNodes = this.collectAllNodes(treeDef);
    const byId = new Map<string, V4NodeDefinition | V4ApotheosisDefinition>();
    for (const node of allNodes) byId.set(node.id, node);

    // Build parent→children map
    const children = new Map<string, string[]>();
    for (const node of allNodes) {
      const parentId = 'parentId' in node ? node.parentId : undefined;
      if (parentId && parentId !== 'root') {
        if (!children.has(parentId)) children.set(parentId, []);
        children.get(parentId)!.push(node.id);
      }
    }
    // Apotheosis nodes have parentIds (plural)
    for (const apo of treeDef.apotheosis) {
      for (const pid of apo.parentIds) {
        if (!children.has(pid)) children.set(pid, []);
        children.get(pid)!.push(apo.id);
      }
    }

    // Assign Y positions based on XP threshold ordering
    const sortedByXp = [...allNodes].sort((a, b) => a.xpThreshold - b.xpThreshold);
    const uniqueThresholds = [...new Set(sortedByXp.map(n => n.xpThreshold))].sort((a, b) => a - b);

    // Determine tier from XP threshold
    const getTier = (xp: number): LayoutTier => {
      if (xp >= 6000) return 'apotheosis';
      if (xp >= 2000) return 'transcendent';
      if (xp >= 500) return 'threshold';
      return 'foundation';
    };

    // Split thresholds: apotheosis gets fixed bottom position, rest fill above
    const apoThresholds = uniqueThresholds.filter(t => getTier(t) === 'apotheosis');
    const mainThresholds = uniqueThresholds.filter(t => getTier(t) !== 'apotheosis');

    const apoReserve = apoThresholds.length > 0 ? 30 : 0; // vertical space reserved for apotheosis
    const mainHeight = bounds.height - 60 - apoReserve;
    const mainSpacing = mainHeight / Math.max(mainThresholds.length - 1, 1);

    const thresholdY = new Map<number, number>();
    for (let i = 0; i < mainThresholds.length; i++) {
      thresholdY.set(mainThresholds[i], bounds.y + 30 + i * mainSpacing);
    }
    // Apotheosis at the bottom with guaranteed gap
    for (const t of apoThresholds) {
      thresholdY.set(t, bounds.y + bounds.height - 30);
    }

    // Assign columns: main trunk nodes get center, fork branches get offset columns
    const centerX = bounds.x + bounds.width / 2;

    // Determine path columns
    const pathOrder: string[] = [];
    for (const node of allNodes) {
      if (node.path && !pathOrder.includes(node.path)) {
        pathOrder.push(node.path);
      }
    }

    // For Fork 1 (3-way), space paths across width
    // For Fork 2 (2-way per path), offset within path column
    const fork1Forks = treeDef.forks.filter(f => f.options.length === 3);
    const fork2Forks = treeDef.forks.filter(f => f.options.length === 2);

    // Assign path columns based on fork 1 (3 paths)
    const pathColumnX = new Map<string, number>();
    if (fork1Forks.length > 0 && pathOrder.length > 0) {
      const nPaths = pathOrder.length;
      const columnSpacing = Math.min(bounds.width / (nPaths + 1), 180);
      for (let i = 0; i < pathOrder.length; i++) {
        pathColumnX.set(pathOrder[i], centerX + (i - (nPaths - 1) / 2) * columnSpacing);
      }
    }

    // For Fork 2 branches, offset within the path column and propagate to descendants
    const nodeColumnX = new Map<string, number>();

    for (const fork of fork2Forks) {
      if (fork.options.length === 2) {
        const opt0 = byId.get(fork.options[0]);
        const path = opt0?.path;
        if (path) {
          const baseX = pathColumnX.get(path) ?? centerX;
          const offsets = [baseX - 40, baseX + 40];
          for (let oi = 0; oi < fork.options.length; oi++) {
            // Propagate offset to this option and all its descendants
            const stack = [fork.options[oi]];
            while (stack.length > 0) {
              const id = stack.pop()!;
              nodeColumnX.set(id, offsets[oi]);
              const kids = children.get(id);
              if (kids) {
                for (const kid of kids) {
                  const kidNode = byId.get(kid);
                  if (kidNode && !('parentIds' in kidNode)) {
                    stack.push(kid);
                  }
                }
              }
            }
          }
        }
      }
    }

    // Position each node
    for (const node of allNodes) {
      const y = thresholdY.get(node.xpThreshold) ?? bounds.y + 30;
      const tier = getTier(node.xpThreshold);
      let x: number;

      // Apotheosis: center on their path column
      if ('parentIds' in node) {
        x = pathColumnX.get(node.path ?? '') ?? centerX;
      }
      // Fork 2 branch node (option or descendant): use propagated offset
      else if (nodeColumnX.has(node.id)) {
        x = nodeColumnX.get(node.id)!;
      }
      // Node with a path: use path column
      else if (node.path && pathColumnX.has(node.path)) {
        x = pathColumnX.get(node.path)!;
      }
      // Shared/pre-fork nodes: center
      else {
        x = centerX;
      }

      result.set(node.id, {
        nodeId: node.id,
        tier,
        x,
        y,
        nodeType: node.nodeType,
      });
    }

    // Post-pass: resolve X overlaps within same Y level
    this.resolveRowOverlaps(result, byId, measureText, fontSize, bounds);

    return result;
  }

  /** Collect all nodes from a V4 tree (foundation + nodes + apotheosis). */
  private collectAllNodes(treeDef: V4TreeDefinition): (V4NodeDefinition | V4ApotheosisDefinition)[] {
    return [treeDef.foundation, ...treeDef.nodes, ...treeDef.apotheosis];
  }

  /** Resolve X overlaps within each Y level. */
  private resolveRowOverlaps(
    result: Map<string, LayoutNode>,
    byId: Map<string, V4NodeDefinition | V4ApotheosisDefinition>,
    measureText: MeasureTextFn,
    fontSize: string,
    bounds: LayoutBounds,
  ): void {
    // Group by Y
    const rowGroups = new Map<number, LayoutNode[]>();
    for (const pos of result.values()) {
      const key = Math.round(pos.y);
      if (!rowGroups.has(key)) rowGroups.set(key, []);
      rowGroups.get(key)!.push(pos);
    }

    const minBound = bounds.x + BOUNDS_MARGIN;
    const maxBound = bounds.x + bounds.width - BOUNDS_MARGIN;

    for (const row of rowGroups.values()) {
      if (row.length < 2) continue;
      row.sort((a, b) => a.x - b.x);

      // Compute minimum gaps — only measure labels for node types that show them persistently
      const gaps: number[] = [];
      const hasLabel = (n: LayoutNode) => n.nodeType === 'active' || n.nodeType === 'evolution';
      for (let i = 0; i < row.length - 1; i++) {
        const leftDef = byId.get(row[i].nodeId);
        const rightDef = byId.get(row[i + 1].nodeId);
        const leftW = hasLabel(row[i]) && leftDef ? measureText(leftDef.name, fontSize) : 0;
        const rightW = hasLabel(row[i + 1]) && rightDef ? measureText(rightDef.name, fontSize) : 0;
        gaps.push(Math.max(MIN_NODE_GAP, (leftW + rightW) / 2 + LABEL_PADDING));
      }

      // Left-to-right sweep
      for (let i = 0; i < row.length - 1; i++) {
        const needed = row[i].x + gaps[i];
        if (row[i + 1].x < needed) {
          row[i + 1].x = needed;
        }
      }

      // Re-center within bounds
      const rowLeft = row[0].x;
      const rowRight = row[row.length - 1].x;
      const rowCenter = (rowLeft + rowRight) / 2;
      const boundsCenter = (minBound + maxBound) / 2;
      const centerShift = boundsCenter - rowCenter;
      for (const pos of row) {
        pos.x += centerShift;
      }

      // Clamp
      if (row[0].x < minBound) {
        const shift = minBound - row[0].x;
        for (const pos of row) pos.x += shift;
      } else if (row[row.length - 1].x > maxBound) {
        const shift = maxBound - row[row.length - 1].x;
        for (const pos of row) pos.x += shift;
      }
    }
  }
}
